import { type FC, useState } from 'react';
import { Badge, Button, Divider, Group, Loader, Paper, Select, type SelectProps, Stack, Text } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { formatCascade, PayoutCascadeQueueItemType } from '~/entities/payout';
import { defaultOptionsFilter } from '~/shared/lib/utils/defaultOptionsFilter';
import { OrderedEntryList, type OrderedEntry } from '~/shared/ui/OrderedEntryList';
import { type QueueEntryValue } from '../lib';

const renderSelectOptionWithId: SelectProps['renderOption'] = ({ option }) => (
    <div>
        <Text size='sm'>{option.label}</Text>
        <Text
            size='xs'
            opacity={0.5}
        >
            {option.value}
        </Text>
    </div>
);

interface CascadeQueueCardProps {
    entries: OrderedEntry<QueueEntryValue>[];
    onEntriesChange: (entries: OrderedEntry<QueueEntryValue>[]) => void;
    groupOptions: { value: string; label: string }[];
    templateOptions: { value: string; label: string }[];
    isOptionsLoading: boolean;
    onAddGroup: (groupId: string) => void;
    onAddTemplate: (templateId: string) => void;
    onClear: () => void;
}

export const CascadeQueueCard: FC<CascadeQueueCardProps> = ({
    entries,
    onEntriesChange,
    groupOptions,
    templateOptions,
    isOptionsLoading,
    onAddGroup,
    onAddTemplate,
    onClear,
}) => {
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    const handleAddGroupClick = () => {
        if (!selectedGroupId) {
            return;
        }

        onAddGroup(selectedGroupId);
        setSelectedGroupId(null);
    };

    const handleAddTemplateClick = () => {
        if (!selectedTemplateId) {
            return;
        }

        onAddTemplate(selectedTemplateId);
        setSelectedTemplateId(null);
    };

    return (
        <Paper
            p='md'
            radius='md'
            withBorder
        >
            <Stack gap='md'>
                <Group justify='space-between'>
                    <Text
                        fw={600}
                        size='sm'
                    >
                        Очередь
                    </Text>

                    <Button
                        size='xs'
                        color='red'
                        variant='outline'
                        leftSection={<IconTrash size={14} />}
                        disabled={entries.length === 0}
                        onClick={onClear}
                    >
                        Очистить очередь
                    </Button>
                </Group>

                <Group
                    align='flex-end'
                    gap='sm'
                    wrap='wrap'
                >
                    <Select
                        flex={1}
                        miw={220}
                        searchable
                        clearable
                        label='Группа'
                        placeholder='Выберите группу'
                        data={groupOptions}
                        value={selectedGroupId}
                        onChange={setSelectedGroupId}
                        filter={defaultOptionsFilter}
                        disabled={isOptionsLoading}
                        renderOption={renderSelectOptionWithId}
                        leftSection={isOptionsLoading ? <Loader size='xs' /> : null}
                    />

                    <Button
                        leftSection={<IconPlus size={16} />}
                        disabled={!selectedGroupId}
                        onClick={handleAddGroupClick}
                    >
                        Добавить группу
                    </Button>
                </Group>

                <Group
                    align='flex-end'
                    gap='sm'
                    wrap='wrap'
                >
                    <Select
                        flex={1}
                        miw={220}
                        searchable
                        clearable
                        label='Шаблон'
                        placeholder='Выберите шаблон'
                        data={templateOptions}
                        value={selectedTemplateId}
                        onChange={setSelectedTemplateId}
                        filter={defaultOptionsFilter}
                        disabled={isOptionsLoading}
                        renderOption={renderSelectOptionWithId}
                        leftSection={isOptionsLoading ? <Loader size='xs' /> : null}
                    />

                    <Button
                        leftSection={<IconPlus size={16} />}
                        disabled={!selectedTemplateId}
                        onClick={handleAddTemplateClick}
                    >
                        Добавить шаблон
                    </Button>
                </Group>

                <Divider />

                <OrderedEntryList
                    entries={entries}
                    onChange={onEntriesChange}
                    emptyMessage='Очередь пуста'
                    renderEntry={(value) => (
                        <Group
                            gap='xs'
                            wrap='nowrap'
                        >
                            <Badge
                                color={
                                    value.type === PayoutCascadeQueueItemType.QUEUE_ITEM_TYPE_TEMPLATE
                                        ? 'grape'
                                        : 'blue'
                                }
                                variant='light'
                            >
                                {formatCascade.queueItemTypeLabel(value.type)}
                            </Badge>

                            <div>
                                <Text size='sm'>{value.name}</Text>
                                <Text
                                    size='xs'
                                    c='dimmed'
                                >
                                    {value.id}
                                </Text>
                            </div>
                        </Group>
                    )}
                />
            </Stack>
        </Paper>
    );
};
